import { useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import { Dialog, DialogContent } from './ui/dialog';
import { COMMAND_PALETTE_ACTIONS } from '@/config/command-palette';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { useCommandPalette } from './CommandPaletteContext';

/** Global command palette dialog — ⌘K / Ctrl+K. */
export function CommandPaletteDialog() {
  const { open, setOpen } = useCommandPalette();
  const navigate = useNavigate();
  const { can } = usePermissions();

  const visibleActions = COMMAND_PALETTE_ACTIONS.filter(
    (a) => !a.requiredPermissions || a.requiredPermissions.some((p) => can(p))
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(!open);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, setOpen]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="overflow-hidden p-0 shadow-lg">
        <Command className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground">
          <div className="flex items-center border-b px-3">
            <Command.Input
              placeholder="Search or jump to…"
              className="flex h-12 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <Command.List className="max-h-[300px] overflow-y-auto p-2">
            <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
              No results found.
            </Command.Empty>
            <Command.Group heading="Actions">
              {visibleActions
                .filter((a) => a.group === 'actions')
                .map((action) => (
                  <Command.Item
                    key={action.id}
                    value={action.label}
                    onSelect={() => {
                      if (action.href) navigate(action.href);
                      setOpen(false);
                    }}
                    className="cursor-pointer rounded-sm px-2 py-1.5 text-sm aria-selected:bg-accent"
                  >
                    {action.label}
                  </Command.Item>
                ))}
            </Command.Group>
            <Command.Group heading="Navigation">
              {visibleActions
                .filter((a) => a.group === 'navigation')
                .map((action) => (
                  <Command.Item
                    key={action.id}
                    value={action.label}
                    onSelect={() => {
                      if (action.href) navigate(action.href);
                      setOpen(false);
                    }}
                    className="cursor-pointer rounded-sm px-2 py-1.5 text-sm aria-selected:bg-accent"
                  >
                    {action.label}
                  </Command.Item>
                ))}
            </Command.Group>
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

type CommandPaletteProviderProps = { children: ReactNode };

/** Mounts command palette dialog alongside children. Use with CommandPaletteContextProvider. */
export function CommandPaletteProvider({ children }: CommandPaletteProviderProps) {
  return (
    <>
      {children}
      <CommandPaletteDialog />
    </>
  );
}

export function CommandPaletteTrigger() {
  const { toggle } = useCommandPalette();

  return (
    <button
      type="button"
      onClick={toggle}
      className="inline-flex h-9 w-full max-w-sm items-center gap-2 rounded-md border border-input bg-background px-3 text-sm text-muted-foreground hover:bg-accent md:w-64"
    >
      <span>Search…</span>
      <kbd className="pointer-events-none ml-auto hidden rounded border bg-muted px-1.5 font-mono text-xs sm:inline">
        ⌘K
      </kbd>
    </button>
  );
}
